import { useState } from 'react';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import sendNodeCommand from 'store/sendNodeCommand';

interface CleanupWorkerDialogProps {
  nodeAgentId: string;
  instanceId: string;
  workerName: string;
  onClose: () => void;
  onConfirm: () => void;
}

export default function CleanupWorkerDialog({
  nodeAgentId,
  instanceId,
  workerName,
  onClose,
  onConfirm,
}: CleanupWorkerDialogProps) {
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await sendNodeCommand(nodeAgentId, 'cleanup_worker', {
        instance_id: instanceId,
      });
      onConfirm();
      onClose();
    } catch (err) {
      console.error('Failed to clean up worker:', err);
      setError('Failed to clean up worker. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setError(null);
    onClose();
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Clean Up Worker</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This will remove worker <strong>{workerName}</strong> from tracking.
          If no other workers share its working directory, the directory will
          also be deleted (including its venv, log file and task run files).
          This cannot be undone.
        </DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" color="error" disabled={isSubmitting}>
          {isSubmitting ? 'Cleaning up\u2026' : 'Clean Up'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
